// Use when a query failed and there is NO data to show: a plain sentence,
// the raw text folded behind "Details", and "Try again". With data still on
// screen, keep the view and add a StaleNotice instead.
//
//   <LoadError what="meetings" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} />
//
// The server not answering (offline, restarting, an agent's gateway down)
// is a neutral state with a cloud icon; anything else is an error tone.
// size page | panel (default) | inline (one left-aligned row, for lists).

import type { ReactNode } from "react";
import { AlertTriangle, ChevronRight, CloudOff, RefreshCw } from "lucide-react";

import { errorDetail, errorText, isUnreachable } from "@/lib/api";

import { Button } from "./Button";
import { cx } from "./controlStyles";
import { EmptyState } from "./EmptyState";

/** The raw text behind a plain sentence, folded; nothing when there is none. */
export function ErrorDetails({ detail, className }: { detail: string | null | undefined; className?: string }) {
  if (!detail) return null;
  return (
    <details className={cx("group mt-2", className)}>
      <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded-control text-sm text-fg-subtle outline-none [@media(hover:hover)]:hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus [&::-webkit-details-marker]:hidden">
        <ChevronRight aria-hidden className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
        Details
      </summary>
      <span className="mt-1 block text-xs text-fg-subtle [overflow-wrap:anywhere]">{detail}</span>
    </details>
  );
}

export interface LoadErrorProps {
  /** What failed to load, in the page's words: "meetings", "Integrations". */
  what: string;
  error: unknown;
  onRetry: () => void;
  /** True while the retry runs (q.isFetching): the button shows a spinner. */
  retrying?: boolean;
  size?: "page" | "panel" | "inline";
  /** Replaces the warning icon of a real failure (an unreachable server keeps the cloud). */
  icon?: ReactNode;
  className?: string;
}

export function LoadError({ what, error, onRetry, retrying, size = "panel", icon, className }: LoadErrorProps) {
  const unreachable = isUnreachable(error);
  const text = errorText(error);
  const detail = errorDetail(error);
  const shownIcon = unreachable ? <CloudOff strokeWidth={1.75} /> : (icon ?? <AlertTriangle strokeWidth={1.75} />);

  if (size === "inline") {
    return (
      <div
        role={unreachable ? "status" : "alert"}
        className={cx("flex flex-wrap items-start gap-x-3 gap-y-1 py-2 text-sm text-fg-muted", className)}
      >
        <span
          aria-hidden
          className={cx("mt-0.5 shrink-0 [&>svg]:h-4 [&>svg]:w-4", unreachable ? "text-fg-subtle" : "text-danger")}
        >
          {shownIcon}
        </span>
        <div className="min-w-0 flex-1">
          <span className="font-medium text-fg">Couldn't load {what}.</span> {text}
          <ErrorDetails detail={detail} className="mt-1" />
        </div>
        <Button variant="ghost" size="sm" onClick={onRetry} loading={retrying} className="-my-1">
          {!retrying && <RefreshCw aria-hidden className="h-3.5 w-3.5" strokeWidth={1.75} />}
          Try again
        </Button>
      </div>
    );
  }

  return (
    <EmptyState
      size={size}
      tone={unreachable ? "default" : "error"}
      icon={shownIcon}
      title={`Couldn't load ${what}`}
      description={
        <>
          {text}
          <ErrorDetails detail={detail} />
        </>
      }
      action={
        <Button variant="secondary" onClick={onRetry} loading={retrying}>
          {!retrying && <RefreshCw aria-hidden className="h-4 w-4" strokeWidth={1.75} />}
          Try again
        </Button>
      }
      className={className}
    />
  );
}
